// src/authz/staffPortalAccess.js
import { useContext, useMemo } from "react";
import { usePermissionsCtx } from "./PermissionsProvider";
import { AuthContext } from "../AuthProvider";
import { canFromGrants } from "./can";
import { STAFF_SCOPES } from "./PermissionScope";
import { useSupportViewAs } from "../supportViewAs/SupportViewAsProvider";

/**
 * Root capabilities that give access to the staff/teacher portal. The dashboard is one of the
 * features inside that portal, not the gate to it. Only ANY/OWN grants count, so a parent's
 * RELATED access never opens the staff portal.
 */
export const STAFF_PORTAL_ENTRY_CHECKS = Object.freeze([
  { resource: "dashboard", action: "view" },
  { resource: "my.environment", action: "view" },
  { resource: "my.teacher-details", action: "view" },
  { resource: "my.classrooms", action: "view" },
  { resource: "my.parent-meetings", action: "view" },
  { resource: "reports.root", action: "view" },
  { resource: "finance.root", action: "view" },
  { resource: "school", action: "read" },
  { resource: "school", action: "manage" },
  { resource: "admin", action: "view" },
]);

export function canEnterStaffPortalFromGrants({ ready, grants, tokenParsed }) {
  return STAFF_PORTAL_ENTRY_CHECKS.some((check) => canFromGrants({
    ready,
    grants,
    tokenParsed,
    resource: check.resource,
    action: check.action,
    scopes: STAFF_SCOPES,
  }));
}

export function useCanEnterStaffPortal() {
  const { ready, grants } = usePermissionsCtx() || {};
  const { keycloak } = useContext(AuthContext);
  const supportViewAs = useSupportViewAs();
  const tokenParsed = supportViewAs?.active ? null : keycloak?.tokenParsed;

  return useMemo(
    () => canEnterStaffPortalFromGrants({ ready, grants, tokenParsed }),
    [ready, grants, tokenParsed]
  );
}
